import type { AllKeys } from 'canvas_states/translation/useTranslation';
import type Country from './payment/Country';
import type { CardCurrencySet } from './payment/Payment';

export enum PartnerType {
  affiliate = 'affiliate',
  airline = 'airline',
  cruise = 'cruise',
  hotel = 'hotel',
  rail = 'rail',
}

export enum LabsPartnerType {
  priorityFlight = 'labs:priorityFlight',
}

export interface ApiKey {
  MARKETING_PAGE?: string;
}

export enum TaxDisplay {
  None = 'none',
  Additional = 'additional',
  Inclusive = 'inclusive',
}

export interface FutureCruiseCreditSettings {
  enabled: boolean;
}

export interface CountryBillingSettings {
  countryCode?: Country['code'];
  states?: string[];
  addressEnabled?: boolean;
}

export interface AffiliatePartner {
  pid: string;
  partnerName: string;
  enabled: boolean;
  singleAirLandingPageHeroImage: string;
  logoImage: string;
  eligibilityWidgetApiKey?: string;
  apiKeys?: ApiKey;
}

export interface Tokenless {
  enabled: boolean;
}

export interface PaymentCardType {
  name: string;
  code: string;
}
export interface RequestProfile {
  apiKey: string;
  profileName: string;
  channelSource: string;
}

export interface Configuration {
  partnerShortName?: string;
  partnerName?: string;
  pax: number;
  pnr: string;
  partnerType?: PartnerType;
  labsPartnerType?: LabsPartnerType;
  enableCanvasExperimentalTheme?: boolean;
  companyCode?: string;
  partnerWebsite?: string;
  language: string;
  usesLanguageSelector?: boolean;
  usesCurrencySelector?: boolean;
  isPointsPlusPayEnabled?: boolean;
  supportedCurrencies?: string[];
  supportedLanguages?: string[];
  extraSeatDisclaimerPaymentPageDisplay?: string[];
  payment?: {
    cvv?: boolean;
    cardTypes?: PaymentCardType[];
    paymentMode?: { tokenless: Tokenless };
    includeCardByCurrency?: CardCurrencySet[];
  };
  currencyFormatSettings?: CurrencyFormatSettings;
  billingSettings?: CountryBillingSettings[];
  fccSettings?: FutureCruiseCreditSettings;
  currency?: string;
  defaultCurrency?: string;
  defaultLanguage: string;

  taxDisplay?: TaxDisplay;
  termsAndConditions?: string;
  termsAndConditionsExtraSeat?: string;
  affiliatePartners?: AffiliatePartner[];
  requestProfiles?: RequestProfile[];
  apiGateway?: string;
  up?: UpConfig;
  showSubsidiaryOperatingCarrier?: boolean;
  canvas?: CanvasConfig;
  productsSettings?: ProductsSettings;
  crossAirline?: CrossAirlineConfig;
  seatShop?: SeatShopConfig;
}

export interface IExperimentVariant {
  id: string;
  weight?: number;
}

export interface ProductsSettings {
  onboardSettings?: OnboardSettings;
  extraSeatSettings?: ExtraSeatSettings;
  checkinSettings?: CheckInSettings;
}

export interface OnboardSettings {
  enabled?: boolean;
  channelSourcesAnyOf?: string[];
}

export interface ExtraSeatSettings {
  splitSeating?: boolean;
}

export interface CheckInSettings {
  url: string;
}

export interface CurrencyFormatSettings {
  grouping: boolean;
  stringFormat: string | null;
  fractionDigits: number;
  currencySpecificFormats: CurrencySpecificFormats[];
}

export interface CurrencySpecificFormats {
  currencyCode: string;
  stringFormat: string | null;
  grouping: boolean;
  languageSpecificFormats: LanguageSpecificFormats[];
}

export interface LanguageSpecificFormats {
  language: string;
  grouping: boolean;
  stringFormat: string | null;
}

export interface UpConfig {
  ampVersion?: AmpVersion;
  componentConfigs: UpComponentConfigs;
  pageConfigs: UpPageConfigs;
  currency: UpCurrencyConfigs;
  isPointPaymentsDisabled: boolean;
}

export interface CanvasConfig {
  priceFormatLanguage?: string;
  bidCard?: BidCardConfigs;
  paymentPage?: PaymentPageConfiguration;
  offersPage?: OffersPageConfiguration;
  confirmationPage?: ConfirmationPageConfig;
  loyalty?: LoyaltyConfiguration;
  hero?: HeroConfig;
  partnerTagManager?: PartnerTagManagerConfig;
  marketingPage?: MarketingPageConfig;
}

export interface PartnerTagManagerConfig {
  enabled: boolean;
  loadedEvent: string;
  url: string;
}

export interface OffersPageConfiguration {
  disableGetPartialExperience: boolean;
  howItWorksTemplate: HowItWorksTemplate;
  splitConfirmationNumber: boolean;
}
export interface HowItWorksTemplate {
  sections: { title?: keyof AllKeys; steps: (keyof AllKeys)[] }[];
}

export interface HeroConfig {
  displayPassengerName?: boolean;
  helpAsDropdown?: boolean;
  helpDefaultExpanded?: boolean;
}

export interface ConfirmationPageConfig {
  excludedViewOffersChannelSources: string[];
}

export interface MarketingPageConfig {
  lookupMode: MarketingPageLookupMode;
}

export interface LoyaltyConfiguration {
  enabled: boolean;
  paymentType?: PaymentType;
  pointsSSOGatewayUrl?: string;
  ssoSource?: string;
}

export interface CrossAirlineConfig {
  ampEnabled: boolean;
}

export interface UpComponentConfigs {
  plusgradeFooter?: UpPlusgradeFooterConfigs;
  eligibilityWidget?: UpEligibilityWidgetConfigs;
}

export interface UpPageConfigs {
  coreShoppingExperiencePage?: UpCoreShoppingExperiencePageConfigs;
  offersPage?: OffersPageConfigs;
}

export interface UpCurrencyConfigs {
  global?: UpCurrencyConfigEntry;
  pointsSpecific?: UpCurrencyConfigEntry;
  currencySpecific?: UpCurrencyConfigEntry[];
}

export interface UpCurrencyConfigEntry {
  currencyCode?: string;
  stringFormat?: string;
  groupingSeparator?: string;
  decimalSeparator?: string;
  fractionDigits?: number;
  currencySpecificFormats?: UpCurrencySpecificFormats[];
}

export interface UpCurrencySpecificFormats {
  currencyCode?: string;
  grouping?: boolean;
  stringFormat?: string;
}

export interface UpRoutingErrorPageConfigs {
  contactButtonUrl?: string;
  displayContactButton?: boolean;
  errorHeroImageUrl?: string;
}

export interface PageConfigsUpgradeTypeModal {
  upgradeType: string;
  enabled: boolean;
}

export interface UpCoreShoppingExperiencePageConfigs {
  disableReviewPriceBreakdown?: boolean;
  upgradeTypeModal?: PageConfigsUpgradeTypeModal[];
  enableMoreAboutSection?: boolean;
  disableCurrentExperienceCard?: boolean;
}

export interface SneakPeekFlowConfigs {
  enabled: boolean;
  onlyDisplayBusinessAndNff?: boolean;
  channelSourcesAnyOf?: string[];
  displayCheckInButton?: boolean;
}

export interface QuickPurchaseConfigs {
  enabled: boolean;
}

export interface StandbyDSBConfigs {
  enabled: boolean;
}

export interface UpUpgradeTypeModal {
  upgradeType: string;
  enabled: boolean;
}

export interface UpCoreShoppingSubmissionStatusPageConfigs {
  redirectionLink?: string;
  fallbackOffer?: { enabled: boolean; channelSource: string };
}

export interface UpSystemErrorPageConfigs {
  contactButtonUrl?: string;
  displayContactButton?: boolean;
  errorHeroImageUrl?: string;
}

export interface UpModifyOfferCardConfigs {
  displayPax?: boolean;
  enableCashCard?: boolean;
}
export interface UpFlightInformationConfigs {
  displayPax?: boolean;
}
export interface UpPnrInfoCardConfigs {
  displayPrimaryPassengerName?: boolean;
}

export interface UpLiveAuctionBidPageConfigs {
  displaySmsModal?: boolean;
  displayPnrInfoCard?: boolean;
  displayNotice?: boolean;
}
export interface UpSplashPageConfigs {
  displayPnrInfoCard?: boolean;
}

export interface PaymentPageConfigs {
  enableIframePayment?: boolean;
}
export interface UpSingleSegmentModifyPageConfigs {
  displayPnrInfoCard?: boolean;
}

export interface UpOfferProcessPageConfigs {
  displayBreadcrumbNavigation?: boolean;
}

export interface UpBreadcrumbNavigationConfigs {
  renderMobileTextKeys?: boolean;
}

export interface UpPlusgradeFooterConfigs {
  displayFooter?: boolean;
  faqUrl?: string;
}

export interface BidCardConfigs {
  hidePlaceButton?: boolean;
}

export interface UpEligibilityWidgetConfigs {
  showPrivacyPolicyCheckbox?: boolean;
}

export interface UpOfferProcessToolTipConfigs {
  enablePurchaseModes?: string[];
}

export interface UpHeroConfigs {
  showLoyaltyLogin?: boolean;
}

export interface UpFlightCardConfigs {
  showOperatingCarrierTextKey?: boolean;
  showMarketingCarrierTextKey?: boolean;
}

export interface IUpWebCheckinPageConfigs {
  intervalPollTimesInSeconds?: number;
  failSafeMaxPollCount?: number;
}

export interface UpStartPageConfigs {
  landingPage: LandingPage;
}

export interface UpMarketingPageConfigs {
  heroImageUrl?: string;
  displayHeroImage?: boolean;
}

export interface OffersPageConfigs {
  boostMyBid?: { incrementPercentage?: number };
}

export interface PaymentPageConfiguration {
  disclaimerWithNoCheckbox?: boolean;
  showEquipmentInfo?: boolean;
  showSecondPaymentCheckBox: ShowSecondPaymentCheckBox;
  binFundingSourceCheck?: { enabled: boolean };
  useCpfNumber?: boolean;
}

export interface ShowSecondPaymentCheckBox {
  enabled?: boolean;
  channelSourcesAnyOf?: string[];
}

export interface SeatShopConfig {
  enabled?: boolean;
  channelSources?: string[];
}

export enum LandingPage {
  CoreShoppingExperience = 'coreShoppingExperience',
}

export enum MarketingPageLookupMode {
  PNR_LASTNAME = 'PNR_LASTNAME',
  FLIGHT_SEAT_LASTNAME = 'FLIGHT_SEAT_LASTNAME',
}

export const enum AmpVersion {
  V1 = 'V1',
  V2 = 'V2',
  V3 = 'V3',
}

export enum PaymentType {
  POINTS = 'POINTS',
  MIXED = 'MIXED',
}
